/*
* DeliverablesReview component specific for Adalia M&E.
* 
* Project can be passed as a prop, but if not, this component expects a project-id context. Rendering without causes an error.
* 
*/
import React from 'react';

import {resolveComponentProps} from '../form_viewer.jsx';

import {
  DataType,
  NumericalValidationLimits,
  castDataType,
} from '../common.jsx';

import Helpers from './helpers.jsx';
import Setting from './setting.jsx';

import TextField from './text_field.jsx';
import { AdaliaMEIndicativePercentageReached } from './adalia_me_deliverables.jsx';
import { AdaliaMEIndicativePercentageReachedV2 } from './adalia_me_deliverables_v2';
import GridLayout from './grid_layout.jsx';
import RepeatableRegion from './repeatable_region.jsx';

import Resolver from '../component_resolver.jsx'


export default class AdaliaMEDeliverablesReview extends GridLayout {

  static get CATEGORY() {
    return 'question';
  }

  static get TYPE() {
    return 'adalia_me_deliverables_review';
  }

  static icon() {
    return <i className='fa fa-tasks'></i>;
  }

  constructor(props) {
    super(props);
    this.addRow = this.addRow.bind(this);
    this.deleteRow = this.deleteRow.bind(this);
    this.addColumn = this.addColumn.bind(this);
    this.deleteColumn = this.deleteColumn.bind(this);
    this.reorderOption = this.reorderOption.bind(this);
    this.state = {}
  }

  static defaultContent(language) {
    let fieldName = 'field_' + Helpers.randomString();

    let repeatableDeliverableReviewRegionContent = Object.assign(AdaliaMERepeatableDeliverableReviewRegion.defaultContent(language), {
      name: fieldName + '_repeatable_deliverable_review_region',
      dependentOptions: fieldName + '_dropdown',
      deletable: false,
      sortable: false,
      allowMultiple: true,
    });

    return Object.assign(super.defaultContent(language), {
      level: 'project',
      name: fieldName,
      rows: [
        [
          {
            span: 1,
            component: repeatableDeliverableReviewRegionContent,
          }
        ]
      ]
    });
  }

  renderSettings() {
    return <></>
  }
}


export class AdaliaMERepeatableDeliverableReviewRegion extends RepeatableRegion {

  static get TYPE() {
    return 'adalia_me_repeatable_deliverable_review_region';
  }

  static get TRANSLATED_ATTRIBUTES() {
    return {
      label: 'field_deliverables_default_label'
    };
  }

  // Can be dependent on the selector selection
  static resolveRelatedAnswerNamesOrIds(content, repeatedFieldScope = "") {
    let names = [];
    if (content.name) {
      names.push(repeatedFieldScope + "[" + content.name + "]");
    }
    let dependentOptions = content.dependentOptions ? ['[' + content.dependentOptions + ']'] : [];
    return [...dependentOptions, ...names];
  }

  static defaultContent(language) {
    return Object.assign(super.defaultContent(language), {
      component: {
        ...Object.assign(GridLayout.defaultContent(language), {
          deletable: false,
          cloneable: false
        }),
        rows: [
          [{ span: 1, component: null }]
        ],
      },
    });
  }

  getSelectedDeliverables(answers, rows, IndicativePercentComponent) {
    return rows.map((rowId) => {
      let answer = answers[rowId] || {};
      let component = Resolver.createElement(IndicativePercentComponent.TYPE,
        resolveComponentProps(
          Object.assign(IndicativePercentComponent.defaultContent(this.props.language), {name: `indicative_percentage_reached`}), {}, {answers: answer}
        )
      );

      return {
        deliverable_id: answer['deliverable_id'],
        content:
          <div key={rowId} className='component-row' style={{display: 'block'}}>
            <div className={'mb-1'}>
              {answer['milestone_title']} - {answer['deliverable_title']}
            </div>
            <div className='component'>{component}</div>
          </div>
      }
    });
  }

  componentDidUpdate(prevProps, prevState) {
    super.componentDidUpdate();
  }

  componentDidMount() {
    super.componentDidMount();

    if (this.state.rows.length == 0) {
      var projectId = this.context?.projectId;
      var rows = [];
      var deliverables = [];

      if (projectId) {
        let answers = {};
        if (typeof this.props.prefillFormProps.answers != 'object') {
          this.props.prefillFormProps.answers = JSON.parse(this.props.prefillFormProps.answers || '{}');
        }

        Object.keys(this.props.prefillFormProps.answers).forEach(key => {
          if (key.endsWith('repeatable_deliverable_region') || key.endsWith('repeatable_deliverable_region_v2')) {
            answers = this.props.prefillFormProps.answers[key];
            let rowKeys = Object.keys(answers).filter(k => k != 'row_count');
            let IndicativePercentComponent = key.endsWith('repeatable_deliverable_region') ?
              AdaliaMEIndicativePercentageReached : AdaliaMEIndicativePercentageReachedV2;

            deliverables = deliverables.concat(this.getSelectedDeliverables(answers, rowKeys, IndicativePercentComponent));
            rows = [...rows, ...rowKeys];
          }
        });

      } else {
        rows.push('row-0');
      }

      this.setState({rows: rows, deliverables: deliverables})
    }
  }

  constructor(props) {
    super(props);
    this.state = {deliverables: {}, rows: []}
  }

  renderView() {
    var rows = [];
    var project = this.props.project;
    var projectId = this.context?.projectId;

    this.state.rows.forEach((rowId, i) => {
      let component = null;
      let fieldName = `${this.fullFieldName()}[${rowId}]`;

      if (this.props.content.component) {
        if ((this.inEditMode || this.props.content.provideReviewedPercentageReached) && this.props.content.component.rows.length == 1)   {
          this.props.content.component.rows.push([{
            span: 1,
            component: Object.assign(AdaliaMEReviewedPercentageReached.defaultContent(this.props.language), {name: `reviewed_percentage_reached`, deletable: false, sortable: false})
          }])
        } else if ((!this.inEditMode && !this.props.content.provideReviewedPercentageReached) && this.props.content.component.rows.length > 1) {
          this.props.content.component.rows = this.props.content.component.rows.filter((r) => r[0]?.component?.type != 'adalia_me_reviewed_percentage_reached')
        }

        component = Resolver.createElement(
          this.props.content.component.type,
          resolveComponentProps(this.props.content.component, this.props, {
            selectCallback: this.props.selectCallback,
            selectedId: this.props.selectedId,
            repeatedFieldScope: this.props.content.name + "[" + rowId + "]",
            repeatedFieldRowIndex: i,
            project: project,
            readOnly: project?.report_approved,
            // Provide only prefill answers relevant for project
            prefillAnswers: (this.props.prefillAnswers || {})[projectId] || {},
            prefillAttachments: (this.props.prefillAttachments || {})[projectId] || {},
          })
        )
      } else if (this.inEditMode) {
        component = <Helpers.DropArea componentId={this.props.content.id} />;
      }

      rows.push(
        <div key={rowId} className='component-row' style={{display: 'block'}}>
          <div className='mb-2'>
            { projectId ? this.state.deliverables[i].content : "<Deliverable Content>"}
          </div>

          <div className='component'>
            {component}
          </div>

          {!this.inEditMode && projectId && (
            <input type="hidden" name={`${fieldName}][deliverable_id]`} value={this.state.deliverables[i].deliverable_id}/>
          )}
        </div>
      );
    });

    return (
      <div>
        {rows.length > 0 &&
          <>
            <label className='mb-1'>{this.getText('label')}</label>
            {rows}
          </>
        }
        <input type='hidden' name={this.fullFieldName() + '[row_count]'} defaultValue={rows.length} />
      </div>
    );
  }

  renderSettings() {
    return <div>
      <Setting.TextField name='label' label={I18n.t('field_label', {scope: 'fe'})} value={this.getText('label')} onChange={this.updateSettings} />
      <Setting.Checkbox name='provideReviewedPercentageReached' value={this.props.content.provideReviewedPercentageReached} onChange={this.updateSettings} label={I18n.t('field_allow_providing_reviewed_percentage_reached', {scope: 'fe'})} description={''} />
    </div>;
  }

  beforeUpdateSettings(content) {
    super.beforeUpdateSettings(content);
    content.provideReviewedPercentageReached = castDataType(content.provideReviewedPercentageReached, DataType.BOOLEAN);
  }
}


export class AdaliaMEReviewedPercentageReached extends TextField {
  static get TYPE() {
    return 'adalia_me_reviewed_percentage_reached';
  }

  static get TRANSLATED_ATTRIBUTES() {
    return {
      label: 'field_default_reviewed_percentage_reached_label',
      description: '',
      placeholder: '',
      help: null,
      unit: '%',
    };
  }

  static defaultContent(language) {
    return Object.assign(super.defaultContent(language), {
      reportKey: '',
      unit: null,
      help: null,
      dataType: DataType.INTEGER,
      deletable: false,
      cloneable: false,
      validations: {dataType: DataType.INTEGER}
    });
  }

  renderSettings() {
    let validations = this.props.content.validations || {};

    return <>
      <Setting.TextField name="label" label={I18n.t('field_label', {scope: 'fe'})} value={this.getText('label')} onChange={this.updateSettings} />
      <Setting.TextField name="placeholder" label={I18n.t('field_placeholder', {scope: 'fe'})} value={this.getText('placeholder')} onChange={this.updateSettings} />
      <Setting.LongTextField name="description" label={I18n.t('field_description', {scope: 'fe'})} value={this.getText('description')} onChange={this.updateSettings} />
      <Setting.LongTextField name="help" label={I18n.t('field_help', {scope: 'fe'})} value={this.getText('help')} onChange={this.updateSettings} />

      <div className="setting">
        <label>
          {I18n.t('validate_range', {scope: 'fe'})}
        </label>
        <div className="validation-range">
          <div className="value">
            <input type="text" ref="validateMin" name="validateMin" className="form-control" defaultValue={validations.min} placeholder={I18n.t('validation_min', {scope: 'fe'})} onKeyUp={this.updateSettings} onBlur={(e)=> {this.props.content.dataType == DataType.DATE && this.updateSettings(e)}} autoComplete={this.getAutoComplete()} />
          </div>
          <div className="dash">
            &mdash;
          </div>
          <div className="value">
            <input type="text" ref="validateMax" name="validateMax" className="form-control" defaultValue={validations.max} placeholder={I18n.t('validation_max', {scope: 'fe'})} onKeyUp={this.updateSettings} onBlur={(e)=> {this.props.content.dataType == DataType.DATE && this.updateSettings(e)}} autoComplete={this.getAutoComplete()} />
          </div>
        </div>
      </div>

      <Setting.TextField
        name="reportKey"
        label={I18n.t('field_report_key', {scope: 'fe'})}
        value={this.props.content.reportKey}
        description={I18n.t('field_report_key_description', {scope: 'fe'})}
        {...(this.allowModifyInSafeMode ? {onChange: this.updateSettings} : {disabled: true})}
      />
    </>
  }

  beforeUpdateSettings(content) {
    content.validations = {};
    content.dataType = DataType.INTEGER;

    if ((content.validateMin || '').length > 0) {
      content.validations.min = castDataType(content.validateMin < NumericalValidationLimits.MIN ? NumericalValidationLimits.MIN : content.validateMin, content.dataType);
    }
    if ((content.validateMax || '').length > 0) {
      content.validations.max = castDataType(content.validateMax > NumericalValidationLimits.MAX ? NumericalValidationLimits.MAX : content.validateMax, content.dataType);
    }
  }
}
